export interface PrequalBranding {
  displayName: string
  logoUrl?: string
  fontFamily?: string
  primary: string
  accent: string
  background: string
  foreground: string
}

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/
const DISPLAY_NAME_MAX = 200
const LOGO_URL_MAX = 2048
const FONT_FAMILY_MAX = 200
const FONT_FAMILY_TOKEN_MAX = 8
const FONT_IDENT = /^[A-Za-z][A-Za-z0-9-]*$/
const FONT_QUOTED = /^["'][A-Za-z][A-Za-z0-9 -]*["']$/
const FONT_UNQUOTED_SPACED = /^[A-Za-z][A-Za-z0-9]*(?: [A-Za-z][A-Za-z0-9]+)+$/

const COLOR_KEYS = ['primary', 'accent', 'background', 'foreground'] as const

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function hexRelativeLuminance(hex: string): number {
  const red = channelLuminance(hex.slice(1, 3))
  const green = channelLuminance(hex.slice(3, 5))
  const blue = channelLuminance(hex.slice(5, 7))
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

function channelLuminance(hexPair: string): number {
  const channel = Number.parseInt(hexPair, 16) / 255
  if (!Number.isFinite(channel)) {
    return 0
  }
  return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

export function contrastingInk(hex: string): string {
  return hexRelativeLuminance(hex) > 0.4 ? '#1A1A1A' : '#FFFFFF'
}

export function normalizeOptionalHexColor(
  value: unknown
): { ok: true; value?: string } | { ok: false; errors: string[] } {
  if (value === undefined) {
    return { ok: true, value: undefined }
  }
  if (value === null || typeof value !== 'string') {
    return { ok: false, errors: ['must be a #RRGGBB color'] }
  }
  const trimmed = value.trim()
  if (trimmed.length === 0 || !HEX_COLOR.test(trimmed)) {
    return { ok: false, errors: ['must be a #RRGGBB color'] }
  }
  return { ok: true, value: trimmed }
}

function normalizeFontFamilyToken(token: string): string | undefined {
  const trimmed = token.trim()
  if (FONT_IDENT.test(trimmed)) {
    return trimmed
  }
  if (FONT_QUOTED.test(trimmed)) {
    return `"${trimmed.slice(1, -1)}"`
  }
  if (FONT_UNQUOTED_SPACED.test(trimmed)) {
    return `"${trimmed}"`
  }
  return undefined
}

export function normalizeOptionalFontFamily(
  value: unknown
): { ok: true; value?: string } | { ok: false; errors: string[] } {
  if (value === undefined || value === null) {
    return { ok: true, value: undefined }
  }
  if (typeof value !== 'string') {
    return { ok: false, errors: ['must be a CSS font-family list'] }
  }
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return { ok: true, value: undefined }
  }
  if (trimmed.length > FONT_FAMILY_MAX) {
    return { ok: false, errors: [`must be at most ${FONT_FAMILY_MAX} characters`] }
  }
  const tokens = trimmed.split(',')
  if (tokens.length > FONT_FAMILY_TOKEN_MAX) {
    return { ok: false, errors: ['must be a CSS font-family list'] }
  }
  const normalized: string[] = []
  for (const token of tokens) {
    const next = normalizeFontFamilyToken(token)
    if (next === undefined) {
      return { ok: false, errors: ['must be a CSS font-family list'] }
    }
    normalized.push(next)
  }
  return { ok: true, value: normalized.join(', ') }
}

export function brandThemeCssProperties(
  branding: PrequalBranding
): Record<string, string> {
  const primaryForeground = contrastingInk(branding.primary)
  const accentForeground = contrastingInk(branding.accent)
  const properties: Record<string, string> = {
    backgroundColor: branding.background,
    color: branding.foreground,
    '--background': branding.background,
    '--foreground': branding.foreground,
    '--primary': branding.primary,
    '--primary-foreground': primaryForeground,
    '--accent': branding.accent,
    '--accent-foreground': accentForeground,
    '--ring': branding.primary
  }
  if (branding.fontFamily !== undefined) {
    properties.fontFamily = branding.fontFamily
    properties['--font-sans'] = branding.fontFamily
  }
  return properties
}

export function normalizePrequalBranding(
  input: unknown
): { ok: true; value: PrequalBranding } | { ok: false; errors: string[] } {
  const errors: string[] = []
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, errors: ['branding must be an object'] }
  }
  const raw = input as Record<string, unknown>
  const displayNameRaw = raw.displayName
  if (typeof displayNameRaw !== 'string') {
    errors.push('branding.displayName is required')
  }
  const displayName =
    typeof displayNameRaw === 'string' ? displayNameRaw.trim() : ''
  if (typeof displayNameRaw === 'string' && displayName.length === 0) {
    errors.push('branding.displayName must not be empty')
  }
  if (displayName.length > DISPLAY_NAME_MAX) {
    errors.push(`branding.displayName must be at most ${DISPLAY_NAME_MAX} characters`)
  }

  const colors: Partial<Record<(typeof COLOR_KEYS)[number], string>> = {}
  for (const key of COLOR_KEYS) {
    const value = raw[key]
    if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
      errors.push(`branding.${key} must be a #RRGGBB color`)
    } else {
      colors[key] = value
    }
  }

  let fontFamily: string | undefined
  const normalizedFontFamily = normalizeOptionalFontFamily(raw.fontFamily)
  if (normalizedFontFamily.ok === false) {
    errors.push(
      ...normalizedFontFamily.errors.map(error => `branding.fontFamily ${error}`)
    )
  } else {
    fontFamily = normalizedFontFamily.value
  }

  let logoUrl: string | undefined
  if (raw.logoUrl !== undefined && raw.logoUrl !== null) {
    if (typeof raw.logoUrl !== 'string') {
      errors.push('branding.logoUrl must be a string')
    } else {
      const trimmed = raw.logoUrl.trim()
      if (trimmed.length === 0) {
        errors.push('branding.logoUrl must not be empty when provided')
      } else if (trimmed.length > LOGO_URL_MAX) {
        errors.push(`branding.logoUrl must be at most ${LOGO_URL_MAX} characters`)
      } else {
        try {
          const parsed = new URL(trimmed)
          if (parsed.protocol !== 'https:') {
            errors.push('branding.logoUrl must use https')
          } else if (parsed.username || parsed.password) {
            errors.push('branding.logoUrl must not include credentials')
          } else {
            logoUrl = trimmed
          }
        } catch {
          errors.push('branding.logoUrl must be a valid URL')
        }
      }
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors }
  }

  const value: PrequalBranding = {
    displayName,
    primary: colors.primary!,
    accent: colors.accent!,
    background: colors.background!,
    foreground: colors.foreground!
  }
  if (logoUrl !== undefined) {
    value.logoUrl = logoUrl
  }
  if (fontFamily !== undefined) {
    value.fontFamily = fontFamily
  }
  return { ok: true, value }
}
